/* RESPONSIBLE TEAM: team-frontend-tech */
/* eslint-disable promise/prefer-await-to-then */
import Modifier from 'ember-modifier';
import { inject as service } from '@ember/service';
import type ReactTeammateAppIslandService from 'embercom/services/react-teammate-app-island';
import { registerDestructor } from '@ember/destroyable';
import { loadScript, loadStylesheet } from 'embercom/lib/dom-utils';
import { getTeammateAppUrls } from 'embercom/lib/react-islands';
import type Session from 'embercom/services/session';
import type InboxVersion from 'embercom/services/inbox-version';

import ENV from 'embercom/config/environment';

const CONTAINER_ID = 'react-component-container';

type TutorialModalOpenerProps = {
  tutorialId: string;
  place: string;
  section: string;
  onOpenChange: (open: boolean) => void;
};

interface Args {
  Positional: [TutorialModalOpenerProps];
}

interface RenderReactComponentSignature {
  Element: Element;
  Args: Args;
}

class RenderReactComponent extends Modifier<RenderReactComponentSignature> {
  @service declare reactTeammateAppIsland: ReactTeammateAppIslandService;
  @service declare appService: $TSFixMe;
  @service declare session: Session;
  @service declare inboxVersion: InboxVersion;

  unmount?: Function;

  constructor(owner: any, args: any) {
    super(owner, args);

    registerDestructor(this, this.cleanup);
  }

  modify(element: Element, [props]: Args['Positional']) {
    this.renderReactComponent(element, props);
  }

  cleanup = () => {
    this.unmount?.();
  };

  async renderReactComponent(element: Element, props: any) {
    if (ENV.environment === 'test') {
      return;
    }

    if (ENV.environment === 'development') {
      if (!this.reactTeammateAppIsland.loadedViteScripts) {
        await Promise.all([
          loadScript('http://embercom.test:4200/a/react-refresh.js'),
          loadScript('http://localhost:5173/r/@vite/client'),
        ]);

        this.reactTeammateAppIsland.loadedViteScripts = true;
      }
    }

    let versionedDeploysAvailable;
    if (this.inboxVersion.isInbox2) {
      versionedDeploysAvailable =
        this.session.isWorkspaceLoaded &&
        this.session.workspace.isFeatureEnabled('react-island-versioned-deploys');
    } else {
      versionedDeploysAvailable = this.appService.app.canUseReactIslandVersionedDeploys;
    }
    let { jsUrl, cssUrl } = getTeammateAppUrls(versionedDeploysAvailable);

    if (!this.reactTeammateAppIsland.loadedEntryPointScript) {
      await loadScript(jsUrl);

      this.reactTeammateAppIsland.loadedEntryPointScript = true;
    }

    if (!window.ReactTeammateApp) {
      throw new Error('ReactTeammateApp not found after loading script');
    }

    let reactComponentMountElement = document.createElement('div');
    reactComponentMountElement.id = CONTAINER_ID;
    reactComponentMountElement.style.position = 'relative';
    reactComponentMountElement.style.zIndex = '9999';

    let shadowRoot = element.attachShadow({ mode: 'open' });
    await loadStylesheet(cssUrl, shadowRoot);
    shadowRoot.appendChild(reactComponentMountElement);

    this.unmount = window.ReactTeammateApp.experimental.renderTutorialModalLauncher(
      reactComponentMountElement,
      props,
    );
  }
}

export default RenderReactComponent;

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'render-react-component': typeof RenderReactComponent;
  }
}
